import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * glør PDP Bundle — UI-Verhalten der Bundle-Auswahl.
 *
 * Diese Komponente ist bewusst duenn. Sie macht genau drei Dinge:
 *
 *   1. Panel der gewaehlten Option zeigen, die der anderen verstecken.
 *   2. Inputs in nicht gewaehlten Panels disablen, damit der Browser sie NICHT
 *      mitschickt (Line Item Properties waeren sonst mehrdeutig).
 *   3. #syncPayload(): die Aggregat-Felder fuellen.
 *
 * Was sie NICHT macht (und nie machen soll): Preise rechnen, Rabatte
 * berechnen, per fetch() in den Warenkorb schreiben oder mehrere Produkte
 * hinzufuegen. Das gehoert serverseitig in die Bundle-App (heute Ghost
 * Bundles, spaeter eine eigene Cart Transform Function). Siehe den doc-Block in
 * blocks/glr-pdp-bundle.liquid.
 *
 * #syncPayload() ist die EINE Stelle, die bei der App-Umstellung angefasst
 * wird — dort entstehen die Werte, die die App/Function liest.
 */
class GlrPdpBundle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    // Serverseitig ist bereits die Standard-Option offen und aktiv; hier wird
    // nur das Aggregat nachgezogen, das ohne JS leer bleibt.
    this.#syncPayload();

    // Preis-Spiegelung: reine ANZEIGE. Es wird nichts gerechnet - die Zahl
    // kommt genau so aus der Einstellung des gewaehlten Sets, wie sie auch im
    // Reiter steht. Ohne JS bleibt der native Preis stehen, das ist korrekt.
    this.mirrorPrice = this.dataset.mirrorPrice === 'true';
    this.mirrorPill = this.dataset.mirrorPill === 'true';
    this.#mirrorPrice();

    // Variantenwechsel: der native variant-picker laedt das Section-HTML neu
    // und dispatcht productSelect. Nur so werden Hero + Komponenten-Grid dieses
    // Blocks aktuell — Horizon rendert beliebige Bloecke NICHT von selbst neu.
    const section = this.closest('.shopify-section, dialog');
    if (section) section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // Direct-Add: den nativen ATC der Buy-Buttons-Form abfangen und alle Teile
    // der gewaehlten Option selbst per AJAX hinzufuegen (siehe #handleFormSubmit).
    // Bei aus bleibt alles beim Alten: Properties an die Form, App expandiert.
    this.directAdd = this.dataset.directAdd === 'true';
    // Verkauft eine Option ein fertiges Bundle-Produkt, muss der ATC auch dann
    // abgefangen werden, wenn Direct-Add aus ist - sonst legte die native Form
    // das Hauptprodukt hinzu. Das Kennzeichen steht an der Option, sobald dort
    // ein Bundle-Produkt gewaehlt ist; ob sich fuer die aktuelle Groesse/Farbe
    // eine Variante finden liess, entscheidet erst der Klick.
    this.hasBundleProduct = Boolean(this.querySelector('[data-glr-bundle-option][data-glr-bundle-product]'));
    if (this.directAdd || this.hasBundleProduct) {
      const scope = section ?? document;
      this.buyForm = scope.querySelector('form[id^="BuyButtons-ProductForm-"]');
      if (this.buyForm) {
        // Klick abfangen (Capture): Horizon fuegt per Button-Klick hinzu
        // (on:click="/handleClick"). preventDefault stoppt zusaetzlich das Submit.
        this.buyForm.addEventListener('click', this.#handleFormClick, true);
        // Submit (Capture): faengt zusaetzlich das Absenden per Enter-Taste ab.
        this.buyForm.addEventListener('submit', this.#handleFormSubmit, true);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);

    const section = this.closest('.shopify-section, dialog');
    if (section) section.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    if (this.buyForm) {
      this.buyForm.removeEventListener('click', this.#handleFormClick, true);
      this.buyForm.removeEventListener('submit', this.#handleFormSubmit, true);
    }
  }

  /**
   * Aktualisiert bei Variantenwechsel NUR die variantenabhaengigen Anzeige-Teile
   * jeder Option (Hero-Bild + Varianten-Titel und das Komponenten-Grid) aus dem
   * frisch gerenderten Section-HTML. Radios, Panel-Sichtbarkeit, Auswahl und die
   * Duft-Slots tragen User-State und werden NICHT angefasst.
   * @param {{ target: EventTarget, promise: Promise<{ detail?: { html?: Document } }> }} event
   */
  #handleProductSelect = (event) => {
    // Aus Produktkarten stammende Events ignorieren (nur das Haupt-PDP-Produkt).
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const { html } = detail;

        // Zuerst ueber die Block-ID, sonst den ersten Bundle-Block im frischen
        // HTML nehmen. Kam die Antwort aus einer anderen Vorlage oder hat
        // Shopify die Block-ID neu vergeben, waere sonst stillschweigend gar
        // nichts aktualisiert worden - ohne jeden Hinweis.
        const fresh =
          html.querySelector(`glr-pdp-bundle[data-block-id="${this.dataset.blockId}"]`) ??
          html.querySelector('glr-pdp-bundle');
        if (!fresh) return;

        // Zuordnung per POSITION statt key: mehrere Optionen duerfen denselben
        // key haben (z. B. zwei Sets mit key "ritual3"). Per-key-Matching wuerde
        // sonst die erste gleichnamige Option erwischen -> Option 2 aktualisiert
        // sich nicht. Current + frisches HTML rendern in gleicher Reihenfolge.
        const freshOptions = Array.from(fresh.querySelectorAll('[data-glr-bundle-option]'));
        this.#options().forEach((option, i) => {
          const freshOption = freshOptions[i];
          if (!(freshOption instanceof HTMLElement)) return;

          // Anzeige-Preis variantenabhaengig nachziehen. Das frische HTML kommt
          // serverseitig gerendert fuer die neue Variante -> nur austauschen,
          // nichts rechnen. Laeuft in JEDEM Modus (auch ohne Hero).
          // Die Bundle-Variante haengt an Groesse und Farbe - nach einem
          // Variantenwechsel steht im frischen HTML die richtige. Ohne dieses
          // Nachziehen legte der Warenkorb weiter die zuerst gerenderte Groesse.
          const freshBundleVariant = freshOption.dataset.glrBundleProductVariant;
          if (freshBundleVariant) option.dataset.glrBundleProductVariant = freshBundleVariant;
          else delete option.dataset.glrBundleProductVariant;

          const currentPrices = option.querySelector('.glr-bundle__prices');
          const freshPrices = freshOption.querySelector('.glr-bundle__prices');
          if (currentPrices && freshPrices) currentPrices.replaceWith(freshPrices);

          // Ersparnis-Box: durchgestrichener Gesamtwert (falls vorhanden).
          const currentSavingsCompare = option.querySelector('.glr-bundle__savings-compare');
          const freshSavingsCompare = freshOption.querySelector('.glr-bundle__savings-compare');
          if (currentSavingsCompare && freshSavingsCompare)
            currentSavingsCompare.replaceWith(freshSavingsCompare);

          // Ersparnis-Pillen (Reiter, Panel-Kopf, Box) koennen je Variante
          // anders lauten (4./5. Spalte in "Preise je Variante") - Text aus
          // dem frischen HTML uebernehmen, die Elemente selbst bleiben.
          for (const sel of ['.glr-bundle__tabpill', '.glr-bundle__pnl-pill', '.glr-bundle__savings-save', '.glr-bundle__savings-pct']) {
            const freshEls = freshOption.querySelectorAll(sel);
            option.querySelectorAll(sel).forEach((el, j) => {
              const freshEl = freshEls[j];
              if (freshEl) el.replaceChildren(...Array.from(freshEl.cloneNode(true).childNodes));
            });
          }

          // Der Hero ist OPTIONAL - seit er sich einzeln abschalten laesst
          // ("Produktbild oben zeigen"), gibt es den Komponenten-Modus auch
          // ohne ihn. Frueher stand hier `if (!currentHero) return;` als
          // Abkuerzung fuer "dann ist ohnehin nichts variantenabhaengig".
          // Genau daran ist die Komponentenliste je Variante gescheitert: ohne
          // Hero wurde das Raster gar nicht erst angefasst. Jede Zutat wird
          // deshalb einzeln geprueft.
          const currentHero = option.querySelector('.glr-bundle__kit-hero');
          const freshHero = freshOption.querySelector('.glr-bundle__kit-hero');
          if (currentHero && freshHero) currentHero.replaceWith(freshHero);

          // Zugaben (z. B. die Garantie) koennen je Groesse andere Produkte
          // sein - Liste aus dem frischen HTML uebernehmen.
          const currentAddons = option.querySelector('.glr-bundle__addons');
          const freshAddons = freshOption.querySelector('.glr-bundle__addons');
          if (currentAddons && freshAddons) currentAddons.replaceWith(freshAddons);

          // Komponenten-Grid (Kit-Cells) austauschen.
          const currentGrid = option.querySelector('.glr-bundle__kit-grid');
          const freshGrid = freshOption.querySelector('.glr-bundle__kit-grid');
          if (currentGrid && freshGrid) currentGrid.replaceWith(freshGrid);

          // Hakenliste (Rueckfall-Modus) ebenfalls - auch sie kann je Variante
          // anders lauten.
          const currentInc = option.querySelector('.glr-bundle__inc');
          const freshInc = freshOption.querySelector('.glr-bundle__inc');
          if (currentInc && freshInc) currentInc.replaceWith(freshInc);

          // "· N items"-Zeile in der Kit-Ueberschrift (nur Anzeige) nachziehen.
          const currentHeading = option.querySelector('.glr-bundle__kit .glr-bundle__inc-heading');
          const freshHeading = freshOption.querySelector('.glr-bundle__kit .glr-bundle__inc-heading');
          if (currentHeading && freshHeading) currentHeading.replaceChildren(...freshHeading.childNodes);

          // Herkunftszeile (nur Theme-Editor) mit austauschen. Sonst zeigt sie
          // nach einem Variantenwechsel weiter den Stand des ersten Renderings
          // - und behauptet "Produkt", waehrend die Kacheln darunter laengst
          // von der Variante kommen.
          const currentSrc = option.querySelector('.glr-bundle__kit-src');
          const freshSrc = freshOption.querySelector('.glr-bundle__kit-src');
          if (currentSrc && freshSrc) currentSrc.replaceChildren(...freshSrc.childNodes);
        });

        // Die Section wurde neu gerendert - der Preis steht wieder nativ da.
        // Also erneut spiegeln, sonst passt er nach einem Variantenwechsel
        // nicht mehr zum gewaehlten Set. Einen Frame spaeter, weil die
        // Sticky-Leiste beim selben Ereignis schreibt: wer zuletzt schreibt,
        // entscheidet.
        requestAnimationFrame(() => this.#mirrorPrice());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[glr-pdp-bundle] Event promise rejected:', error);
      });
  };

  /** @returns {HTMLElement[]} */
  #options() {
    return Array.from(this.querySelectorAll('[data-glr-bundle-option]'));
  }

  /** @returns {HTMLElement | null} Die aktuell gewaehlte Option. */
  #selected() {
    const checked = this.querySelector('[data-glr-bundle-radio]:checked');
    return checked instanceof HTMLElement ? checked.closest('[data-glr-bundle-option]') : null;
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-glr-bundle-radio]')) {
      this.#applySelection();
      this.#mirrorPrice();
    }

    if (target.matches('[data-glr-bundle-radio], [data-glr-scent]')) {
      this.#syncPayload();
    }
  };

  /**
   * Panels auf- bzw. zuklappen und Inputs der nicht gewaehlten Optionen
   * abschalten. `disabled` ist hier der eigentliche Mechanismus: der Browser
   * schickt disabled Felder nicht mit.
   */
  #applySelection() {
    const selected = this.#selected();

    for (const option of this.#options()) {
      const isSelected = option === selected;
      const panel = option.querySelector('[data-glr-bundle-panel]');
      if (!(panel instanceof HTMLElement)) continue;

      panel.hidden = !isSelected;

      for (const field of panel.querySelectorAll('fieldset')) {
        field.disabled = !isSelected;
      }
    }
  }

  /**
   * Alle Preis-Anzeigen der Produktseite - die des Themes und die der
   * Sticky-Leiste. Preise in Produktkarten und im Bundle selbst bleiben
   * aussen vor.
   * @returns {HTMLElement[]}
   */
  #priceTargets() {
    const section = this.closest('.shopify-section') ?? document;
    const nodes = [
      ...section.querySelectorAll('[ref="priceContainer"]'),
      ...document.querySelectorAll('[data-glr-sbuy-price]'),
    ];
    return nodes.filter(
      (el) => el instanceof HTMLElement && !el.closest('product-card') && !el.closest('glr-pdp-bundle')
    );
  }

  /**
   * Schreibt den Preis des gewaehlten Sets in die Preis-Anzeigen der Seite.
   *
   * Bewusst KEINE Rechnung: die Betraege werden als Text aus dem bereits
   * gerenderten Reiter uebernommen. Der native Preis wird vorher im Element
   * hinterlegt und zurueckgeschrieben, sobald ein Set ohne eigenen Preis
   * gewaehlt ist - so geht die Ausgangslage nie verloren.
   */
  #mirrorPrice() {
    if (!this.mirrorPrice) return;

    const selected = this.#selected();
    const prices = selected?.querySelector('.glr-bundle__prices');
    // Rabatt-Pille des gewaehlten Sets - dieselbe Beschriftung wie am Reiter,
    // damit oben und unten nicht zwei verschiedene Zahlen stehen.
    const pillText = this.mirrorPill ? selected?.querySelector('.glr-bundle__tabpill')?.textContent?.trim() ?? '' : '';
    const amount = prices?.querySelector('.glr-bundle__price')?.textContent?.trim() ?? '';
    const compare = prices?.querySelector('.glr-bundle__compare')?.textContent?.trim() ?? '';

    for (const target of this.#priceTargets()) {
      // Die Sticky-Leiste hat nur ein Textfeld und pflegt ihren Preis bei
      // Variantenwechsel selbst. Deshalb hier nichts hinterlegen und nichts
      // zurueckschreiben - ohne Set-Preis bleibt einfach ihr eigener stehen.
      const isSticky = target.matches('[data-glr-sbuy-price]');

      if (!amount) {
        if (!isSticky && target.dataset.glrPriceOriginal != null) {
          target.innerHTML = target.dataset.glrPriceOriginal;
        }
        continue;
      }

      if (isSticky) {
        target.textContent = amount;
        continue;
      }

      if (target.dataset.glrPriceOriginal == null) target.dataset.glrPriceOriginal = target.innerHTML;
      target.replaceChildren(this.#priceMarkup(amount, compare, pillText));
    }
  }

  /**
   * Baut die Preis-Anzeige mit den Klassen des Themes, damit Schriftgrad und
   * Farbe unveraendert bleiben. Betraege gehen als Text hinein, nie als HTML.
   * @param {string} amount
   * @param {string} compare
   * @param {string} [pillText]
   * @returns {HTMLElement}
   */
  #priceMarkup(amount, compare, pillText) {
    const box = document.createElement('div');
    box.dataset.glrPriceMirror = 'true';

    const pill = () => {
      if (!pillText) return null;
      const el = document.createElement('span');
      el.className = 'glr-bundle__price-pill';
      el.textContent = pillText;
      return el;
    };

    if (!compare) {
      box.className = 'price__regular';
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = amount;
      box.append(price);
      const p = pill();
      if (p) box.append(p);
      return box;
    }

    box.className = 'price__sale';
    const oldGroup = document.createElement('span');
    oldGroup.className = 'price-item__group';
    const oldPrice = document.createElement('span');
    oldPrice.className = 'price-item--regular compare-at-price';
    oldPrice.textContent = compare;
    oldGroup.append(oldPrice);

    const newGroup = document.createElement('span');
    newGroup.className = 'price-item__group';
    const newPrice = document.createElement('span');
    newPrice.className = 'price-item--sale price';
    newPrice.textContent = amount;
    newGroup.append(newPrice);

    box.append(oldGroup, newGroup);
    const p = pill();
    if (p) box.append(p);
    return box;
  }

  /**
   * ==================================================================
   * SEAM — hier entsteht die Nutzlast fuer die Bundle-Mechanik.
   * ==================================================================
   * Heute (Ghost Bundles) wie spaeter (eigene App / Cart Transform Function)
   * legt der Kunde das ELTERN-Produkt mit diesen Properties in den Warenkorb;
   * die Expansion in die Komponenten passiert serverseitig.
   *
   * Beim Wechsel auf die eigene App wird NUR diese Methode (und ggf. die
   * name-Attribute der zugehoerigen Hidden-Inputs im Liquid) angepasst — die
   * UI darueber bleibt unveraendert.
   */
  #syncPayload() {
    const selected = this.#selected();

    const nameField = this.querySelector('[data-glr-bundle-name]');
    if (nameField instanceof HTMLInputElement) {
      nameField.value = selected?.dataset.glrBundleName ?? '';
    }

    // Je Slot: den gewaehlten Duft in sein Hidden-Feld schreiben (kundenlesbar).
    // Die Radios selbst sind reine UI und tragen keinen properties[...]-Namen —
    // sonst kollidierten gleichnamige Slots zweier Bundles zu EINER Radio-Gruppe.
    for (const option of this.#options()) {
      for (const field of option.querySelectorAll('[data-glr-scent-value]')) {
        if (!(field instanceof HTMLInputElement)) continue;
        const slot = field.dataset.glrSlot;
        const picked = option.querySelector(`[data-glr-scent][data-glr-slot="${slot}"]:checked`);
        if (picked instanceof HTMLInputElement) field.value = picked.value;
      }
    }

    const variantsField = this.querySelector('[data-glr-bundle-variants]');
    if (variantsField instanceof HTMLInputElement) {
      variantsField.value = this.#selectedScentVariantIds(selected).join(',');
    }
  }

  /**
   * Varianten-IDs der gewaehlten Duefte in Slot-Reihenfolge.
   * @param {Element | null} option
   * @returns {string[]}
   */
  #selectedScentVariantIds(option) {
    if (!option) return [];

    const checked = Array.from(option.querySelectorAll('[data-glr-scent]:checked'));

    return checked
      .filter((input) => input instanceof HTMLInputElement)
      .sort((a, b) => Number(a.dataset.glrSlot ?? 0) - Number(b.dataset.glrSlot ?? 0))
      .map((input) => input.dataset.variantId ?? '')
      .filter(Boolean);
  }

  /**
   * ==================================================================
   * DIRECT-ADD — legt alle Teile der gewaehlten Option selbst in den Warenkorb.
   * ==================================================================
   * Nur aktiv, wenn das Setting `direct_add` an ist (data-direct-add="true").
   * Faengt den nativen Add-to-Cart der Buy-Buttons-Form ab. Durch
   * preventDefault() + stopImmediatePropagation() laeuft der Theme-Handler
   * (product-form-component on:submit) NICHT -> das Elternprodukt wird NICHT
   * zusaetzlich hinzugefuegt (kein Doppel-Add des Kamins). Die Bundle-App-
   * Expansion entfaellt in diesem Modus bewusst: wir fuegen alles selbst hinzu.
   * @param {SubmitEvent} event
   */
  /**
   * Klick auf den Add-to-Cart-Button abfangen (Capture, vor Horizons on:click).
   * Nur der echte Add-Button — NICHT Menge, NICHT Express-Checkout. preventDefault
   * verhindert das anschliessende Form-Submit, stopImmediatePropagation Horizons
   * Klick-Handler -> kein Doppel-Add des Kamins.
   * @param {MouseEvent} event
   */
  /**
   * Varianten-ID des fertigen Bundle-Produkts der gewaehlten Option, sonst ''.
   * Ist sie gesetzt, verkauft die Option genau dieses Produkt - das
   * Hauptprodukt der Seite und alle Einzelteile bleiben aussen vor.
   * @returns {string}
   */
  #bundleProductVariant() {
    const selected = this.#selected();
    if (!(selected instanceof HTMLElement)) return '';
    // Bundle mit Duftauswahl: der gewaehlte Duft bestimmt die Bundle-Variante.
    const map = (selected.dataset.glrBundleVariantMap ?? '').toLowerCase();
    if (map) {
      const picked = selected.querySelector('[data-glr-scent]:checked');
      if (picked instanceof HTMLInputElement) {
        const key = '|' + picked.value.trim().toLowerCase() + '=';
        const at = map.indexOf(key);
        if (at >= 0) {
          const rest = map.slice(at + key.length);
          const id = rest.slice(0, rest.indexOf('|'));
          if (id) return id;
        }
      }
    }
    return (selected.dataset.glrBundleProductVariant ?? '').trim();
  }

  #handleFormClick = (event) => {
    // Der Bundle-Produkt-Modus MUSS abfangen, auch wenn Direct-Add aus ist:
    // sonst legte die native Form das Hauptprodukt hinzu statt des Bundles.
    if (!this.directAdd && !this.#bundleProductVariant()) return;
    if (!(this.buyForm instanceof HTMLFormElement)) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const addBtn = target.closest('[name="add"], button.add-to-cart-button, [ref="addToCartButton"]');
    if (!(addBtn instanceof HTMLElement) || !this.buyForm.contains(addBtn)) return;
    // Express-Checkout (ShopPay/PayPal/GPay) nie abfangen.
    if (addBtn.closest('.shopify-payment-button, accelerated-checkout, [data-shopify="dynamic-checkout-cart"]')) return;

    const idField = this.buyForm.querySelector('[name="id"]');
    const kaminId = idField instanceof HTMLInputElement ? idField.value.trim() : '';
    if (!kaminId) return; // ohne gueltige Variante: natives Verhalten lassen

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#directAdd(kaminId, this.buyForm, addBtn);
  };

  #handleFormSubmit = (event) => {
    if (!this.directAdd && !this.#bundleProductVariant()) return;
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const idField = form.querySelector('[name="id"]');
    const kaminId = idField instanceof HTMLInputElement ? idField.value.trim() : '';
    // Ohne gueltige Kamin-Variante: natives Verhalten NICHT stoeren (Fallback).
    if (!kaminId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    const fallback = form.querySelector('[type="submit"]');
    const btn = submitter ?? (fallback instanceof HTMLElement ? fallback : null);
    this.#directAdd(kaminId, form, btn);
  };

  /**
   * "id:menge,id:menge" -> Line-Items. Quelle: data-glr-bundle-fixed der Option.
   * @param {string | undefined} raw
   * @returns {{id:string, quantity:number}[]}
   */
  #parseFixed(raw) {
    return (raw || '')
      .trim()
      .split(',')
      .map((part) => {
        const [id, qty] = part.split(':');
        return { id: (id || '').trim(), quantity: parseInt(qty, 10) || 1 };
      })
      .filter((it) => it.id);
  }

  #openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  /**
   * Baut die Items der gewaehlten Option und legt sie per AJAX in den Warenkorb.
   * Reihenfolge: Kamin (native Variante) -> gewaehlte Duefte (Slot-Reihenfolge)
   * -> Fixed-Produkte + Gratis-Gifts. Muster 1:1 aus assets/glr-cart-set.js:
   * optimistisches CartLinesUpdateEvent, fetch(cart_add_url), optional Rabatt
   * via cart_update_url, danach Cart-Drawer oeffnen.
   * @param {string} kaminId
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} btn
   */
  async #directAdd(kaminId, form, btn) {
    if (this.adding) return;
    this.adding = true;
    if (btn) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }

    const errEl = form.querySelector('.product-form-text__error');
    if (errEl instanceof HTMLElement) {
      errEl.textContent = '';
      errEl.classList.add('hidden');
    }

    const selected = this.#selected();
    const bundleVariantId = this.#bundleProductVariant();

    // Fertiges Bundle-Produkt: EINE Zeile. Kein Hauptprodukt, keine Duefte,
    // keine festen Produkte - die stecken bereits im Bundle. Wuerden wir sie
    // zusaetzlich hinzufuegen, laege dieselbe Ware doppelt im Warenkorb.
    const items = bundleVariantId ? [{ id: bundleVariantId, quantity: 1 }] : [{ id: kaminId, quantity: 1 }];
    if (bundleVariantId && selected instanceof HTMLElement) {
      /** @type {Record<string,string>} */
      const bundleProps = {};

      // Welches Set gewaehlt wurde, steht schon in der Variante des
      // Bundle-Produkts ("Ritual / LA\VIK Studio · 28 cm / Black"). Der
      // Checkout zeigt sie von sich aus; im Warenkorb ergaenzt sie
      // glr-cart-bundles.js. Eine zusaetzliche Property waere im Checkout
      // dieselbe Angabe ein zweites Mal.

      // Was drinsteckt - falls die Option das ausgibt. Bei einem echten
      // Shopify-Bundle listet Horizon die Komponenten ohnehin selbst; dann
      // schaltet man es in der Option besser ab, sonst steht es doppelt da.
      const label = (selected.dataset.glrBundleContentsLabel ?? '').trim();
      const contents = (selected.dataset.glrBundleContents ?? '').trim();
      // Eigene Checkout-Zeilen („Duftoel: 1× Nordic Forest · …") aus der
      // Option, je Zeile „Name: Wert". Shopify zeigt jede Eigenschaft im
      // Checkout als eigene Zeile - so steht der Inhalt dort untereinander.
      const lines = (selected.dataset.glrBundleLines ?? '')
        .split('\n')
        .map((l) => {
          const i = l.indexOf(':');
          return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : null;
        })
        .filter((kv) => kv && kv[0] && kv[1] && !kv[0].startsWith('_'));
      if (contents && lines.length) {
        for (const [k, v] of lines) bundleProps[k] = v;
        bundleProps._glr_contents = lines.map(([k]) => k).join('|');
        // Fuer den Warenkorb: dieselbe Liste wie bei einem Shopify-Bundle.
        bundleProps._glr_contents_list = contents;
      } else if (label && contents) {
        // Sichtbar: steht so im Checkout, in der Bestellung und in der Mail.
        bundleProps[label] = contents;
        // Unsichtbar: sagt glr-cart-bundles.js, welche Eigenschaft der
        // Lieferumfang ist - der Warenkorb zeigt sie dann als Liste, so wie
        // die Bestandteile eines Shopify-Bundles.
        bundleProps._glr_contents = label;
      }

      if (Object.keys(bundleProps).length) items[0].properties = bundleProps;
    }
    if (!bundleVariantId) {
      for (const vid of this.#selectedScentVariantIds(selected)) items.push({ id: vid, quantity: 1 });
      const fixedRaw = selected instanceof HTMLElement ? selected.dataset.glrBundleFixed : '';
      for (const it of this.#parseFixed(fixedRaw)) items.push(it);
    }

    // Bundle-Gruppierung: gemeinsame Line-Item-Property fuer alle Teile dieses
    // Bundles. Cart-Transform (merge) gruppiert danach; Underscore = fuer den
    // Kunden im Warenkorb/Checkout unsichtbar.
    // Nur im Einzelteil-Modus noetig. Beim fertigen Bundle-Produkt gibt es
    // nichts zu gruppieren - Shopify kennt die Zeile bereits als Bundle.
    if (!bundleVariantId) {
      const gid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const bundleTitle = (selected instanceof HTMLElement ? selected.dataset.glrBundleName ?? '' : '').trim();
      items.forEach((it, i) => {
        it.properties = Object.assign({}, it.properties, {
          _glr_bundle: gid,
          _glr_bundle_role: i === 0 ? 'parent' : 'child',
        });
        if (i === 0 && bundleTitle) it.properties._glr_bundle_title = bundleTitle;
      });
    }

    // Im Bundle-Produkt-Modus KEIN Rabattcode: der Set-Preis steckt bereits in
    // der Variante. Ein zusaetzlicher Code zoege denselben Nachlass ein zweites
    // Mal ab - der Kunde zahlte zu wenig, und niemand saehe es sofort.
    const discount = bundleVariantId
      ? ''
      : (selected instanceof HTMLElement ? selected.dataset.glrBundleDiscount ?? '' : '').trim();

    // Erweiterungen (z. B. Early-Access-Geschenk) koennen hier Zeilen
    // anhaengen. So landet alles in EINER Anfrage und der Warenkorb wird nur
    // einmal neu gezeichnet - kein Nachrutschen durch eine zweite Zeile.
    try {
      document.dispatchEvent(new CustomEvent('glr:bundle:before-add', { detail: { items, option: selected, bundleVariantId } }));
    } catch (e) {}

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    /** @type {{items:Object[], sections?:string}} */
    const body = { items: items.map((i) => ({ id: i.id, quantity: i.quantity, properties: i.properties })) };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    // Event best-effort fuer den Drawer-Refresh; scheitert es, laeuft der Rest.
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      const emitter = btn ?? this;
      emitter.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (e) {
      deferred = null;
    }

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.status) {
        if (deferred) deferred.reject?.(new Error(data.description || data.message || 'Add failed'));
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      // Optional: Rabattcode je Option auf den Warenkorb anwenden.
      let discountSections = null;
      if (discount) {
        try {
          // Clear-then-apply: zuerst evtl. haengenden Fremd-/Vorgaenger-Code leeren,
          // DANN unseren eigenen Code setzen. Strikt sequentiell (await), damit das
          // Leeren vor dem Setzen abgeschlossen ist -> ein neues Bundle erbt nie den
          // Code eines vorherigen. Nur bei echtem eigenem Code (discount vorhanden).
          // Clear NUR, wenn ein FRUEHERER eigener Bundle-Code hinterlegt ist —
          // so wird ein veralteter Bundle-Code ersetzt, fremde/Subscription-Codes
          // bleiben unangetastet.
          let _prevBundleCode = '';
          try { _prevBundleCode = sessionStorage.getItem('glr_bundle_discount') || ''; } catch (e) {}
          if (_prevBundleCode && _prevBundleCode !== discount) {
            await fetch(Theme.routes.cart_update_url, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: '' }),
            });
          }
          const dbody = { discount };
          if (sectionIds.length) dbody.sections = sectionIds.join(',');
          const dres = await fetch(Theme.routes.cart_update_url, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(dbody),
          });
          const dj = await dres.json();
          const notApplicable = Array.isArray(dj.discount_codes)
            && dj.discount_codes.some((d) => d.code === discount && d.applicable === false);
          if (notApplicable) {
            console.warn('[glr-pdp-bundle] Rabattcode nicht anwendbar auf den Warenkorb:', discount, dj.discount_codes);
            if (errEl instanceof HTMLElement) {
              errEl.textContent = this.dataset.discountError || ('Rabattcode „' + discount + '" ist nicht gültig.');
              errEl.classList.remove('hidden');
            }
          } else {
            // Angewandter Code merken, damit der globale glør Bundle-Rabatt-Wächter
            // ihn später wieder entfernt, sobald die Bundle-Produkte den Warenkorb
            // verlassen (assets/glr-bundle-discount-guard.js). Nur DIESER Code gilt
            // als "unserer" -> keine fremden Codes betroffen.
            try { sessionStorage.setItem('glr_bundle_discount', discount); } catch (e) { /* egal */ }
            if (dj && dj.sections) {
              // Post-Rabatt gerenderte Sektionen -> Drawer zeigt den Rabatt sofort (ohne Reload).
              discountSections = dj.sections;
            }
          }
        } catch (e) {
          console.warn('[glr-pdp-bundle] Rabattcode konnte nicht angewendet werden:', e);
        }
      }

      if (deferred) {
        const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
        const ajaxCart = await cartRes.json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { items: ajaxCart.items, source: 'glr-pdp-bundle', sections: discountSections || data.sections, didError: false },
        });
      }

      // Garantiert oeffnen — unabhaengig vom auto_open_cart_drawer-Setting.
      this.#openDrawer();
    } catch (err) {
      console.error('[glr-pdp-bundle] Direct-Add fehlgeschlagen:', err);
      if (errEl instanceof HTMLElement) {
        errEl.textContent = err && err.message ? err.message : (this.dataset.errorText || 'Konnte nicht in den Warenkorb gelegt werden.');
        errEl.classList.remove('hidden');
      }
    } finally {
      this.adding = false;
      if (btn) {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('glr-pdp-bundle')) {
  customElements.define('glr-pdp-bundle', GlrPdpBundle);
}
